/**
 * Calculadora Dutching 2FBs.
 *
 * Reparte N selecciones (de 2 a 5) para que el retorno sea el mismo gane la que gane, usando
 * freebets en las patas que se quiera y saldo real para cubrir el resto. Es la generalizacion a
 * N selecciones de la hoja `Gastar dos FreeBet al mismo tiempo.ods` (hoja `Freebet Calc
 * LibreOffice`), que resuelve el caso N = 3 con 2 freebets; con N = 3 este modelo reproduce la
 * hoja al decimal.
 *
 * Modelo (§4 del doc de tasks, `odd/tasks/dutching-2fbs.md`):
 *
 *     K      = f1 · (q1 - 1)                   retorno comun, lo fija la seleccion 1
 *     s1     = 0                               la seleccion 1 se cubre solo con freebet
 *     s_k    = (K - f_k · (q_k - 1)) / q_k     k = 2..N
 *     S      = suma de s_k                     total de saldo real a apostar
 *     P      = K - S                           beneficio garantizado
 *     rating = P / suma de f_k                 rating medio de las freebets usadas juntas
 *
 * Una freebet NO devuelve el importe apostado: si gana, paga `f_k · (q_k - 1)`. De ahi sale K.
 * El retorno si gana la seleccion k es `f_k · (q_k - 1) + s_k · q_k`, que vale K en todas por
 * construccion: eso es exactamente la ecualizacion.
 *
 * Aqui NO hay tramos, ni perdidas bonificables, ni punto fijo (a diferencia de la Dutching
 * Cashback): son cuatro formulas cerradas y exactas.
 *
 * "Autocontenida" (patron B) significa que no se toca el UI kit ni hay que republicarlo; el
 * envoltorio comun `Calculators` se usa igual que en el resto para tener las pestanas y el layout.
 */
import { useSearchParams } from 'react-router-dom';

import Calculators from './Calculators';

/** Motivo por el que no hay reparto cuando alguna cantidad a apostar sale negativa (§4.4). */
export const AVISO_STAKE_NEGATIVO =
  'La freebet de la selección 1 tiene que ser la que mayor retorno genere: con estas cuotas, otra selección la supera y su cantidad a apostar saldría negativa.';

/** Motivo por el que no hay reparto cuando no se ha escrito ninguna freebet. */
export const AVISO_SIN_FREEBET =
  'Escribe la freebet de la selección 1: es la que fija el retorno objetivo que tienen que cubrir las demás.';

/** Cuantas selecciones admite la calculadora (§2, decision 2). */
export const MIN_SELECCIONES = 2;
export const MAX_SELECCIONES = 5;
export const SELECCIONES_POR_DEFECTO = 3;

/**
 * Retorno de una freebet si gana su seleccion: la freebet no devuelve el importe apostado.
 *
 * @param {number} freebet Importe de la freebet.
 * @param {number} odds Cuota decimal de esa seleccion.
 * @returns {number} `freebet · (odds - 1)`.
 */
export const retornoFreebet = (freebet, odds) => freebet * (odds - 1);

/**
 * Saldo real que hay que poner en una seleccion para que su escenario pague el retorno objetivo.
 *
 * @param {number} retornoObjetivo El retorno comun `K`.
 * @param {number} odds Cuota decimal de esa seleccion.
 * @param {number} freebet Freebet apostada en esa seleccion (0 si no lleva).
 * @returns {number} La cantidad a apostar, sin redondear. Negativa si la freebet de esa
 *   seleccion genera mas que el objetivo, que es justo el caso que se bloquea (§4.4).
 */
export const stakeSeleccion = (retornoObjetivo, odds, freebet) =>
  (retornoObjetivo - retornoFreebet(freebet, odds)) / odds;

/** Acepta coma decimal: aqui se escribe 1,8182. */
export const aNumero = (valor) => {
  const parsed = Number.parseFloat(String(valor).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Redondeo a centimos. Misma formula que `round2` de la Dutching Cashback, para que las dos
 * calculadoras redondeen igual. `Math.round` redondea las mitades hacia arriba.
 */
const round2 = (value) => Math.round(value * 100) / 100;

/**
 * `-0` y `0` son el mismo numero, pero `Intl` formatea `-0` como «-0,00 €». Se normaliza para que
 * una cantidad que el redondeo deja en cero no acabe pintada con signo en la tabla.
 *
 * @param {number} valor
 * @returns {number} `0` si el valor es cero (incluido `-0`); el valor tal cual si no.
 */
const sinCeroNegativo = (valor) => (valor === 0 ? 0 : valor);

/**
 * Si el desfase por redondeo merece mostrarse: se comprueba con el MISMO redondeo que usa el
 * formateador de pantalla, para no anunciar un desfase que luego se escribe como `0,00`.
 *
 * @param {number} spread Diferencia entre el mejor y el peor escenario.
 * @returns {boolean}
 */
export const desfaseVisible = (spread) => Math.round(spread * 100) > 0;

/**
 * Numeros de seleccion (1-based) cuya cuota falta o no sirve para apostar.
 *
 * @param {Array<{odds: unknown}>} crudas Las selecciones tal cual salen de los campos, que llegan
 *   con coma decimal.
 * @returns {number[]} Numeros de seleccion, en orden.
 */
export const cuotasFaltantes = (crudas) =>
  crudas.reduce((acc, seleccion, i) => (aNumero(seleccion.odds) > 1 ? acc : [...acc, i + 1]), []);

/** Lista de numeros en castellano: `2`, `2 y 3`, `2, 3 y 4`. */
const enumerar = (numeros) =>
  numeros.length === 1 ? `${numeros[0]}` : `${numeros.slice(0, -1).join(', ')} y ${numeros[numeros.length - 1]}`;

/**
 * Texto del aviso de cuotas incompletas, en singular o en plural segun cuantas falten.
 *
 * @param {number[]} faltantes Numeros de seleccion devueltos por `cuotasFaltantes`.
 * @returns {string}
 */
export const textoCuotaFaltante = (faltantes) =>
  faltantes.length === 1
    ? `Completa la cuota de la selección ${faltantes[0]}.`
    : `Completa la cuota de las selecciones ${enumerar(faltantes)}.`;

/**
 * Motivo por el que el reparto esta bloqueado (§4.4).
 *
 * Las dos causas llevan mensajes distintos porque se arreglan de forma distinta. Y manda la causa
 * raiz: sin freebet en la seleccion 1 no hay retorno objetivo, asi que TODAS las demas patas salen
 * negativas. En ese caso decir «la seleccion 2 saldria negativa» seria cierto pero despistaria; lo
 * que falta es el dato que fija el objetivo.
 *
 * @param {{selecciones: Array<{freebet: number}>}} resultado
 * @returns {string} '' si el reparto no esta bloqueado.
 */
export const textoBloqueo = (resultado) =>
  resultado.selecciones[0].freebet > 0 ? AVISO_STAKE_NEGATIVO : AVISO_SIN_FREEBET;

/**
 * Resuelve el reparto de las N selecciones.
 *
 * @param {Array<{odds: number, freebet: number}>} selecciones Las N selecciones. La primera es la
 *   referencia (decisión 3): es la unica que no lleva saldo real.
 * @param {{ roundStakes?: boolean }} [options] `roundStakes: false` devuelve la capa exacta, sin
 *   redondear, que es la de los valores de oro de §5.
 * @returns {object} Las selecciones ya resueltas, el retorno comun, los totales, el beneficio, el
 *   rating, el desfase y si el reparto esta bloqueado.
 */
export function computeDutching2Fbs(selecciones, { roundStakes = true } = {}) {
  const normalizadas = selecciones.map((seleccion, i) => ({
    numero: i + 1,
    odds: Number(seleccion.odds) || 0,
    freebet: Number(seleccion.freebet) || 0,
    esReferencia: i === 0,
  }));

  // El retorno comun lo fija la seleccion 1: es la pata que se cubre enteramente con freebet.
  const K = retornoFreebet(normalizadas[0].freebet, normalizadas[0].odds);

  // §4.2: se resuelve SIN redondear y el redondeo va despues. La seleccion 1 no lleva saldo real,
  // asi que se fija a 0 en vez de calcularlo: no hay nada que redondearle al usuario.
  const brutas = normalizadas.map((seleccion, i) =>
    i === 0 ? 0 : stakeSeleccion(K, seleccion.odds, seleccion.freebet)
  );
  const stakes = roundStakes ? brutas.map((cantidad, i) => (i === 0 ? 0 : sinCeroNegativo(round2(cantidad)))) : brutas;

  const total = stakes.reduce((acc, cantidad) => acc + cantidad, 0);
  const sumFreebets = normalizadas.reduce((acc, seleccion) => acc + seleccion.freebet, 0);

  // El retorno se RECALCULA con las cantidades definitivas: si se redondea, el desfase aparece aquí.
  const resueltas = normalizadas.map((seleccion, i) => {
    const retorno = retornoFreebet(seleccion.freebet, seleccion.odds) + stakes[i] * seleccion.odds;
    return { ...seleccion, stake: stakes[i], retorno, beneficio: retorno - total };
  });

  const retornos = resueltas.map((seleccion) => seleccion.retorno);
  const spread = Math.max(...retornos) - Math.min(...retornos);
  // El beneficio que se muestra es el GARANTIZADO: el peor escenario. Es lo que la hoja llama
  // «benefici garantit» y lo que calcula con MIN. Sin redondear coincide con K - S.
  const profit = Math.min(...retornos) - total;

  // §4.4: una cantidad negativa significa que esa pata no se puede cubrir con saldo real, porque
  // la freebet de la selección 1 no es la que más retorno genera. El bloqueo se decide sobre las
  // cantidades que se van a colocar DE VERDAD: en la capa redondeada, -0,002 € es 0,00 €, así que
  // no bloquea. Sería incoherente tolerar hasta 8 céntimos de desfase por redondeo y bloquear por
  // dos décimas de céntimo. En la capa exacta no hay redondeo, así que allí la cantidad ES -0,002 €
  // y sí bloquea.
  const colocables = roundStakes ? stakes : brutas;
  const stakesNegativos = colocables.reduce((acc, cantidad, i) => (i > 0 && cantidad < 0 ? [...acc, i + 1] : acc), []);

  return {
    selecciones: resueltas,
    K,
    total,
    sumFreebets,
    profit,
    rating: sumFreebets > 0 ? profit / sumFreebets : 0,
    spread,
    bloqueada: stakesNegativos.length > 0 || sumFreebets === 0,
    stakesNegativos,
  };
}

// --- Interfaz -----------------------------------------------------------------------

const EUR = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const PORCENTAJE = new Intl.NumberFormat('es-ES', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Claves en la URL: la URL es el bus de estado, como en el resto de calculadoras. */
const claves = (i) => ({ odds: `d2f_cuota${i}`, freebet: `d2f_freebet${i}` });

const parametro = (params, key, fallback = '') => {
  const value = params.get(key);
  return value === null || value === '' ? fallback : value;
};

/**
 * Deja sólo dígitos y un único separador decimal (que aquí se escribe con coma).
 *
 * Es el equivalente de `type="number"` sin su problema: con el valor controlado desde la URL,
 * `type="number"` devuelve `""` mientras se teclea el estado intermedio "1,", así que el campo se
 * borra y no hay forma de escribir un decimal. Filtrando en el onChange se bloquean las letras
 * igual y los decimales siguen funcionando.
 */
export const soloNumeros = (valor) => {
  const limpio = valor.replace(/[^0-9.,]/g, '');
  const separador = limpio.search(/[.,]/);
  if (separador === -1) return limpio;
  return limpio.slice(0, separador + 1) + limpio.slice(separador + 1).replace(/[.,]/g, '');
};

const celdaClase = 'px-2 py-1 text-center border-black-4 border-t-2';
const encabezadoClase = 'px-2 py-1 text-center font-semibold text-sm text-black';
// Las cabeceras cortas («Selección», «Cuota», «Freebet») no se parten en dos líneas: la columna se
// mide por el campo que contiene, así que el rótulo se cortaría. En las de resultados NO se aplica:
// allí hay rótulos largos («Cantidad a apostar») que sí conviene poder repartir.
const encabezadoConfigClase = `${encabezadoClase} whitespace-nowrap`;
const etiquetaPieClase = `${celdaClase} text-sm text-black`;
// Franja «cebra» del cuerpo: el sombreado cae en las filas PARES, así la fila 1 sale del mismo
// color que el encabezado y no quedan dos filas seguidas del mismo tono. Son seis columnas de
// números alineados y, sin franja, seguir una fila de izquierda a derecha cuesta.
//
// Los dos tonos viven aquí y sólo aquí, y tienen que ir juntos: en el tema claro la tarjeta es
// `--color-white` (#FFFFFF) y la sombra es el gris `--color-black-2` (#F2F2F4); en el oscuro la
// tarjeta pasa a #161b22 y ese gris claro ya no se distingue del fondo, así que la franja lleva su
// par oscuro `--color-black-11` (#313338). Con un solo tono la alternancia desaparecería al
// cambiar de tema.
const franjaFilaClase = 'bg-black-2 dark:bg-black-11';
// Los campos llevan altura fija: un `select` y un `input` con el mismo padding y borde NO quedan
// igual de altos, porque cada uno trae su propia altura intrínseca.
const campoBase = 'h-9 rounded-xl border-black-4 border-2 p-1 bg-white dark:text-accent-dark dark:bg-white';
const campoClase = `w-full ${campoBase}`;
// La cuota y la freebet comparten ancho fijo y centrado: el ancho es el mismo que la Dutching y que
// la Dutching Cashback (80 px), y medidos por su contenido los recuadros se encojen y se estiran al
// teclear. El ancho vive SÓLO en esta constante, para no acabar con dos utilidades de ancho en el
// mismo elemento (el orden en que Tailwind las emite no es el del atributo, y ya mordió tres veces
// en la DC). Es el mismo patrón que `inputCuotaComisionClase` de la Dutching Cashback: los campos
// que se ven igual comparten constante.
const inputCuotaFreebetClase = `w-[80px] ${campoBase} text-center`;
const selectClase = `${campoClase} text-left`;
const etiquetaPanelClase = 'font-semibold text-sm text-black text-left';
const NEGATIVO = 'text-warning-red font-semibold';
const POSITIVO = 'text-secondary font-semibold';
/**
 * Clase con la que se pinta un valor según su signo.
 *
 * El color lo decide el signo y NADA más: el mismo valor en positivo y en negativo tiene que
 * pintarse con el mismo tamaño. Un `text-sm` metido a mano en una sola de las dos ramas dejaba el
 * beneficio positivo más pequeño que el negativo, y se vio en pantalla antes que en los tests. El
 * tamaño entra por parámetro y lo reciben las dos ramas, así que la asimetría no se puede colar.
 *
 * @param {number} valor
 * @param {string} [tamano] Utilidad de tamaño, si el sitio la necesita. Va a las dos ramas.
 * @returns {string}
 */
const claseSegunSigno = (valor, tamano = '') =>
  `${valor < 0 ? NEGATIVO : POSITIVO}${tamano === '' ? '' : ` ${tamano}`}`;

/**
 * La tarjeta de la calculadora.
 *
 * Todo el estado vive en la URL (§6): el número de selecciones, las cuotas y las freebets. Eso
 * hace que un caso concreto se pueda reproducir pegando el enlace, que es lo que permitió cazar
 * el bug de descomposición de la Dutching Cashback desde una captura de pantalla.
 */
export const Dutching2FbsCard = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const setParametro = (key, value) => {
    const params = new URLSearchParams(searchParams);
    params.set(key, value);
    // `replace`: con 5 selecciones y 2 campos cada una, un `push` por tecla llenaría el historial.
    setSearchParams(params, { replace: true });
  };

  const setNumero = (key, valor) => setParametro(key, soloNumeros(valor));

  const selecciones = Math.min(
    MAX_SELECCIONES,
    Math.max(
      MIN_SELECCIONES,
      Math.round(aNumero(parametro(searchParams, 'd2f_n', SELECCIONES_POR_DEFECTO))) || SELECCIONES_POR_DEFECTO
    )
  );

  // Los textos CRUDOS de la URL, en orden: así se distingue un campo vacío de un 0 escrito.
  const crudas = Array.from({ length: selecciones }, (_, i) => {
    const k = claves(i + 1);
    return { odds: parametro(searchParams, k.odds), freebet: parametro(searchParams, k.freebet) };
  });

  const faltantes = cuotasFaltantes(crudas);
  const resultado =
    faltantes.length > 0
      ? null
      : computeDutching2Fbs(crudas.map((cruda) => ({ odds: aNumero(cruda.odds), freebet: aNumero(cruda.freebet) })));

  // §3.3: el bloqueo no puede convivir con los números, porque los totales que salen por debajo
  // (con una cantidad negativa) son ficticios. Pero los CAMPOS tienen que seguir vivos: sin ellos
  // el usuario no puede arreglar la entrada que provocó el bloqueo. Así que lo que se apaga son
  // los valores calculados —las tres columnas de resultados y el pie— y el resumen, que se
  // sustituye por el aviso.
  const presentable = resultado !== null && !resultado.bloqueada;

  const filaSeleccion = (cruda, i) => {
    const k = claves(i + 1);
    const resuelta = resultado === null ? null : resultado.selecciones[i];

    // La franja cebra: `i` es 0-based, así que las filas pares de verdad son los índices impares.
    return (
      <tr key={i} className={i % 2 === 1 ? franjaFilaClase : undefined}>
        <td className={celdaClase}>{i + 1}</td>
        <td className={celdaClase}>
          <input
            className={inputCuotaFreebetClase}
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={cruda.odds}
            onChange={(event) => setNumero(k.odds, event.target.value)}
          />
        </td>
        <td className={celdaClase}>
          <input
            className={inputCuotaFreebetClase}
            type="text"
            inputMode="decimal"
            placeholder="0,00"
            value={cruda.freebet}
            onChange={(event) => setNumero(k.freebet, event.target.value)}
          />
        </td>
        {/* La selección 1 es la referencia y no lleva saldo real: se muestra un guion. Y si el
            reparto está bloqueado tampoco se pinta la cantidad: una cantidad negativa es
            justamente lo que bloqueó, así que mostrarla sería enseñar el número que delata el
            problema como si fuera una apuesta válida. */}
        <td className={presentable && !resuelta.esReferencia ? `${celdaClase} font-semibold` : celdaClase}>
          {presentable && !resuelta.esReferencia ? EUR.format(resuelta.stake) : '-'}
        </td>
        <td className={celdaClase}>{presentable ? EUR.format(resuelta.retorno) : '-'}</td>
        <td className={celdaClase}>
          {presentable ? (
            <span className={claseSegunSigno(resuelta.beneficio)}>{EUR.format(resuelta.beneficio)}</span>
          ) : (
            '-'
          )}
        </td>
      </tr>
    );
  };

  return (
    <div className="flex flex-col shadow rounded-2xl bg-white mx-auto w-full" style={{ maxWidth: '1100px' }}>
      <div className="font-medium px-4 py-3 bg-black text-white text-center text-base capitalize rounded-2xl rounded-b-none">
        Dutching 2FBs
      </div>

      <div className="flex flex-col md:flex-row md:items-end gap-4 px-5 py-4 my-3 mx-3 bg-primary-2 rounded-2xl">
        <label className="flex w-full max-w-[16rem] flex-col gap-1 dark:text-accent-dark">
          <span className={etiquetaPanelClase}>Número de selecciones</span>
          <select
            className={selectClase}
            value={selecciones}
            onChange={(event) => setParametro('d2f_n', event.target.value)}>
            {Array.from({ length: MAX_SELECCIONES - MIN_SELECCIONES + 1 }, (_, i) => MIN_SELECCIONES + i).map(
              (valor) => (
                <option key={valor} value={valor}>
                  {valor}
                </option>
              )
            )}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto px-3">
        <table className="w-full" style={{ minWidth: '860px' }}>
          <thead>
            <tr>
              <th className={encabezadoConfigClase}>Selección</th>
              <th className={encabezadoConfigClase}>Cuota</th>
              <th className={encabezadoConfigClase}>Freebet</th>
              <th className={encabezadoClase}>Cantidad a apostar</th>
              <th className={encabezadoClase}>Retorno bruto</th>
              <th className={encabezadoClase}>Beneficio</th>
            </tr>
          </thead>
          <tbody>{crudas.map(filaSeleccion)}</tbody>
          <tfoot>
            <tr>
              {/* La etiqueta ocupa las tres primeras columnas y el importe cae en la cuarta, bajo
                  «Cantidad a apostar»: así el total no queda debajo de una columna de resultados. */}
              <td className={etiquetaPieClase} colSpan={3}>
                Cantidad Total Apostada:
              </td>
              <td className={celdaClase}>{presentable ? EUR.format(resultado.total) : '-'}</td>
              <td className={celdaClase} colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>

      {resultado === null && (
        <p className="text-center text-black-7 py-10 px-5">{textoCuotaFaltante(faltantes)} para ver el resultado.</p>
      )}

      {resultado !== null && !presentable && (
        <p className="text-center font-semibold py-10 px-5 text-warning-red">{textoBloqueo(resultado)}</p>
      )}

      {presentable && (
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 px-5 py-4 mb-3 mt-2 mx-3 bg-primary-2 rounded-2xl">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-black dark:text-accent-dark">Beneficio garantizado:</span>
            <span className={claseSegunSigno(resultado.profit, 'text-xl')}>{EUR.format(resultado.profit)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm text-black dark:text-accent-dark">
              Rating medio de las freebets usadas juntas:
            </span>
            <span className={claseSegunSigno(resultado.rating, 'text-xl')}>{PORCENTAJE.format(resultado.rating)}</span>
          </div>
          {desfaseVisible(resultado.spread) && (
            <div className="flex items-center gap-2">
              <span className="font-semibold text-sm text-black dark:text-accent-dark">
                Desfase por redondeo a céntimos:
              </span>
              <span className="dark:text-accent-dark">{EUR.format(resultado.spread)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Página de la calculadora.
 *
 * La tarjeta va dentro de `Calculators`, que aporta las pestañas superiores, el wizard y el layout
 * que comparten todas las calculadoras. "Autocontenida" (patrón B) significa que no se toca el
 * UI kit ni hay que republicarlo: el envoltorio común se usa igual que en el resto.
 */
const Dutching2Fbs = () => (
  <Calculators>
    <Dutching2FbsCard />
  </Calculators>
);

export default Dutching2Fbs;
